import { isPlatformServer, isPlatformBrowser } from '@angular/common';
import { HostListener, Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, delay, distinctUntilChanged } from 'rxjs/operators';
import { AppState } from './app-state';

@Injectable({
  providedIn: 'root',
})
export class StateService {
  state$ = new BehaviorSubject<AppState>(new AppState());
  private storageKey = 'appState';
  constructor() {
    const persisted = localStorage.getItem(this.storageKey);
    const update = { initialized: true, service: this };
    if (persisted) {
      Object.assign(update, JSON.parse(persisted));
    }
    this.set(update);
  }

  @HostListener('window:notification', ['$event'])
  onNotification(event: CustomEvent) {
    this.set({
      notifications: [...this.get('notifications'), event.detail],
    });
  }

  /*
    Any State property as an observable, distinct until changed
  */
  select<T = any>(key: keyof AppState): Observable<T> {
    return this.state$.pipe(
      map((state: any) => state[key] as T),
      delay(0),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
    );
  }

  /*
    Current value of any State property
  */
  get<T = any>(key: keyof AppState): T {
    return this.state$.getValue()[key] as any as T;
  }

  set(changes: Partial<AppState>): void {
    this.state$.next(
      Object.assign(new AppState(), this.state$.getValue(), changes),
    );
    const toPersist = AppState.persistedKeys.reduce(
      (obj, key) => ({
        ...obj,
        ...(key in changes ? { [key]: changes[key] } : {}),
      }),
      {},
    );
    if (Object.keys(toPersist).length) {
      const oldPersisted = localStorage.getItem(this.storageKey);
      const newPersisted = {
        ...(oldPersisted ? JSON.parse(oldPersisted) : {}),
        ...toPersist,
      };
      localStorage.setItem(this.storageKey, JSON.stringify(newPersisted));
    }
  }
}
